import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { formsApi, projectsApi } from '@/lib/api';

interface NewFormModalProps {
  /** Preset and locked when opened from a project tab. */
  projectId?: string | null;
  projectName?: string;
  onClose: () => void;
}

/** Title + scope, then straight into the builder. Everything else is edited there. */
export function NewFormModal({ projectId: presetProjectId, projectName, onClose }: NewFormModalProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<'project' | 'global'>(presetProjectId ? 'project' : 'global');
  const [projectId, setProjectId] = useState<string>(presetProjectId ?? '');
  const [error, setError] = useState<string | null>(null);
  const locked = !!presetProjectId;

  const { data: projects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list(),
    enabled: !locked,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      formsApi.create({
        title: title.trim(),
        projectId: scope === 'project' ? projectId : null,
      }),
    onSuccess: (form) => {
      queryClient.invalidateQueries({ queryKey: ['projectForms'] });
      queryClient.invalidateQueries({ queryKey: ['formsManage'] });
      navigate(`/forms/manage/${form.id}`);
      onClose();
    },
    onError: (err: any) => setError(err?.message || "Couldn't create the form"),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return setError('Give the form a title.');
    if (scope === 'project' && !projectId) return setError('Pick a project, or make the form global.');
    setError(null);
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-xl shadow-xl">
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2 className="text-heading-3 text-secondary">New form</h2>
            <p className="text-sm text-grey-500 mt-1">You'll add the questions next.</p>
          </div>
          <button onClick={onClose} className="p-2 -m-2 text-grey-400 hover:text-secondary rounded transition-colors" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
          <div>
            <label htmlFor="new-form-title" className="block text-sm font-medium text-secondary mb-1.5">
              Title
            </label>
            <input
              id="new-form-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Client onboarding questionnaire"
              autoFocus
              className="input w-full"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-secondary mb-1.5">Where does it live?</span>
            {locked ? (
              <p className="text-sm text-grey-600">
                In <strong>{projectName || 'this project'}</strong>. Project members can be assigned it, or you can open it to everyone in the project.
              </p>
            ) : (
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="scope" checked={scope === 'global'} onChange={() => setScope('global')} className="mt-1 w-4 h-4 text-accent border-grey-300 focus:ring-accent" />
                  <span className="text-sm">
                    <span className="font-medium text-secondary">Global</span>
                    <span className="block text-grey-500">Not tied to a project. Reaches people only when you assign it — e.g. onboarding.</span>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="radio" name="scope" checked={scope === 'project'} onChange={() => setScope('project')} className="mt-1 w-4 h-4 text-accent border-grey-300 focus:ring-accent" />
                  <span className="text-sm flex-1">
                    <span className="font-medium text-secondary">In a project</span>
                    <span className="block text-grey-500">Shows on the project's Forms tab; can be opened to all its members.</span>
                    {scope === 'project' && (
                      <select value={projectId} onChange={(e) => setProjectId(e.target.value)} className="input mt-2 w-full" aria-label="Project">
                        <option value="">Choose a project…</option>
                        {(projects ?? []).map((p: any) => (
                          <option key={p.id} value={p.id}>
                            {p.name}
                          </option>
                        ))}
                      </select>
                    )}
                  </span>
                </label>
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-error" role="alert">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              Create and add questions
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
